import datesInRange from 'common/utils/Dates/datesInRange';
import nearestDay from 'common/utils/Dates/nearestDay';
import moment from 'moment';
import { isNaN, isNumber, maxBy } from 'lodash';
import {
  MINIMUM_PAID_DURATION_EIGHT_MIN_RULE,
  BILLING_INTERVAL_EIGHT_MIN_RULE,
  CMS_EIGHT_MIN_RULE,
} from 'src/pages/invoices/constants';

function filterInsurancesByDateRange(insurances, startDate, endDate, excludeExpiredDates = false) {
  return insurances?.filter((insurance) => {
    const enrolledAt = insurance?.enrolled_at && nearestDay(moment(insurance.enrolled_at));
    const expiredAt = excludeExpiredDates ? null :
      insurance?.expired_at && nearestDay(moment(insurance.expired_at));
    return enrolledAt && datesInRange(enrolledAt, expiredAt, startDate, endDate);
  });
}

function getLatestStartingInsurance(insurances) {
  return maxBy(insurances, (insurance) => insurance?.enrolled_at);
}

function findInsuranceByDateRange({
  insurances,
  startDate,
  endDate,
  ignoreSocialCareExpiredDates,
  serviceAuthorization,
  setInsuranceExpired = () => {},
}) {
  if (!insurances) return null;
  if (!startDate) return null;

  setInsuranceExpired(false);

  // Prioritize service authorization insurance if provided
  const candidateInsurances = serviceAuthorization ?
    [insurances?.find((insurance) => insurance.id === serviceAuthorization.insurance.id)] : insurances;

  // Tier 1: Check both start and end dates
  const validInsurancesForDateRange = filterInsurancesByDateRange(candidateInsurances, startDate, endDate);
  let latestStartingInsurance = getLatestStartingInsurance(validInsurancesForDateRange);

  // Tier 2: Ignore expired dates if specified and no valid insurance found
  if (ignoreSocialCareExpiredDates && !latestStartingInsurance) {
    const validInsurancesIncludingExpired = filterInsurancesByDateRange(candidateInsurances, startDate, endDate, true);
    latestStartingInsurance = getLatestStartingInsurance(validInsurancesIncludingExpired);
    setInsuranceExpired(!!latestStartingInsurance);
  }

  return latestStartingInsurance || null;
}

function requestDateOutOfRange(requestDate, startDate, endDate) {
  if (!requestDate) return false;
  const requestMoment = moment(requestDate);
  return (startDate && requestMoment.isBefore(moment(startDate))) ||
    (endDate && requestMoment.isAfter(moment(endDate)));
}

function formatTimeForApi(hour, minute, ampm) {
  const parsedHour = parseInt(hour, 10);
  const parsedMinute = parseInt(minute, 10);
  if ([parsedHour, parsedMinute].some((v) => !isNumber(v) || isNaN(v))) return null;
  if (!['AM', 'PM'].includes(ampm)) return null;

  let formattedHour = parsedHour;
  if (ampm === 'PM' && formattedHour < 12) {
    formattedHour += 12;
  } else if (ampm === 'AM' && formattedHour === 12) {
    formattedHour = 0;
  }

  return `${formattedHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')}:00`;
}

function parseTimeForForm(timeString) {
  if (!timeString) return { hour: null, minute: null, ampm: null };
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);

  let ampm = 'AM';
  let hour12 = hour;

  if (hour >= 12) {
    ampm = 'PM';
    hour12 = hour === 12 ? 12 : hour - 12;
  } else if (hour === 0) {
    hour12 = 12;
  }

  return {
    hour: hour12.toString().padStart(2, '0'),
    minute: minutes,
    ampm,
  };
}

function getBillableUnits(
  unitAmountCalculationHint,
  serviceDuration,
  remainingAuthorizedAmount,
) {
  const duration = parseInt(serviceDuration, 10);
  if (unitAmountCalculationHint !== CMS_EIGHT_MIN_RULE || isNaN(duration)) return null;

  let billableUnits = 0;
  if (duration >= MINIMUM_PAID_DURATION_EIGHT_MIN_RULE) {
    billableUnits = 1 + Math.floor((duration - MINIMUM_PAID_DURATION_EIGHT_MIN_RULE) / BILLING_INTERVAL_EIGHT_MIN_RULE);
  }

  return (!remainingAuthorizedAmount && remainingAuthorizedAmount !== 0) ?
    billableUnits : Math.min(billableUnits, remainingAuthorizedAmount);
}

export {
  findInsuranceByDateRange,
  requestDateOutOfRange,
  formatTimeForApi,
  parseTimeForForm,
  getBillableUnits,
};
